import { addDays, format, isBefore, isAfter } from 'date-fns';
import { type MonetaryValue } from 'ezmoney';

import { latestAllowedDate } from 'modules/finance-review/models/payable/payableRequest';

import { type PaymentComplianceResult } from './paymentCompliance';
import {
  type PaymentToSchedule,
  executeOnIndividualDateFlag,
} from '../../models';
import { PaymentMethod } from '../../models/paymentMethod';
import { ukWireTransferSchedulingLimit } from '../../transfer/models';

export interface Payment {
  amount: MonetaryValue;
  executeOn: string;
  billId: string;
}

const fromPaymentToSchedule = (
  paymentToSchedule: PaymentToSchedule,
): Payment => {
  return {
    billId: paymentToSchedule.bill.id,
    amount: paymentToSchedule.amount,
    executeOn: paymentToSchedule.dueDate,
  };
};

type GetPaymentsToScheduleParams = {
  paymentsToSchedule: PaymentToSchedule[];
  paymentComplianceResults?: PaymentComplianceResult[];
  payAt: Date | typeof executeOnIndividualDateFlag;
  today: Date;
  paymentMethod: PaymentMethod;
  isUkCompany: boolean;
  isSchedulingUkTransfersFeatureEnabled: boolean;
};
/**
 *
 * At the moment, the only case where a payment is "not compliant" is when the amount is too high.
 * In this case, the back end would have returned in the compliantPaymentsSuggestion property how to split the payment in multiple payments.
 * for example, if the payment is of 150k, it might be split into 2 payments of 99k and 51k.
 */
export const getValidPaymentsToSchedule = ({
  paymentsToSchedule,
  paymentComplianceResults,
  ...rest
}: GetPaymentsToScheduleParams): Payment[] => {
  if (
    paymentComplianceResults === undefined ||
    paymentsToSchedule.length !== paymentComplianceResults.length
  ) {
    return paymentsToSchedule.map((paymentToSchedule) =>
      toPayment({ paymentToSchedule, ...rest }),
    );
  }

  const payments = paymentsToSchedule.map((paymentToSchedule, index) => {
    const paymentComplianceResult = (paymentComplianceResults ?? [])[index];

    return paymentComplianceResult.isCompliant
      ? toPayment({ paymentToSchedule, ...rest })
      : paymentComplianceResult.compliantPaymentsSuggestion;
  });

  return payments.flat();
};

type ToPaymentParams = {
  paymentToSchedule: PaymentToSchedule;
  payAt: Date | typeof executeOnIndividualDateFlag;
  today: Date;
  paymentMethod: PaymentMethod;
  isUkCompany: boolean;
  isSchedulingUkTransfersFeatureEnabled: boolean;
};
const toPayment = ({
  paymentToSchedule,
  payAt,
  today,
  paymentMethod,
  isUkCompany,
  isSchedulingUkTransfersFeatureEnabled,
}: ToPaymentParams): Payment => {
  const payment = fromPaymentToSchedule(paymentToSchedule);
  const maxSchedulingDate = getMaxSchedulingDate({
    today,
    paymentMethod,
    isUkCompany,
    isSchedulingUkTransfersFeatureEnabled,
  });

  let executeOn =
    payAt === executeOnIndividualDateFlag
      ? paymentToSchedule.dueDate
      : format(payAt, 'yyyy-MM-dd');

  // Only the front-end knows what is « today's date »
  // Hence it is responsible to align the date on today if the date is
  // in the past. If we do this check on the back-end side without
  // knowing the timezone, we could potentially increase the date by one
  // day because the user is actually in a GMT-12 timezone
  if (isBefore(new Date(executeOn), today)) {
    executeOn = format(today, 'yyyy-MM-dd');
  }
  if (isAfter(new Date(executeOn), maxSchedulingDate)) {
    executeOn = format(maxSchedulingDate, 'yyyy-MM-dd');
  }
  return {
    ...payment,
    executeOn,
  };
};

export const getMaxSchedulingDate = ({
  paymentMethod,
  isUkCompany,
  isSchedulingUkTransfersFeatureEnabled,
  today,
}: {
  today: Date;
  paymentMethod: PaymentMethod;
  isUkCompany: boolean;
  isSchedulingUkTransfersFeatureEnabled: boolean;
}): Date => {
  if (
    paymentMethod === PaymentMethod.WireTransfer &&
    isUkCompany &&
    isSchedulingUkTransfersFeatureEnabled
  ) {
    return addDays(today, ukWireTransferSchedulingLimit);
  }

  return latestAllowedDate;
};
